import * as firebase from 'firebase/app';
import gql from 'graphql-tag';
import graphqlClient from '@shared/graphql/client';
import firebaseInit from '@shared/firebase/firebase-init';

export default class AuthService {
  static init() {
    return firebaseInit();
  }

  static onAuthStateChanged(
    callbackSuccess,
    callbackError,
  ) {
    return firebase
      .auth()
      .onAuthStateChanged(callbackSuccess, callbackError);
  }

  static async sendEmailVerification(authenticationUser, originUrl) {
    if (await this.isEmailConfigured()) {
      return this.sendEmailVerificationFromBackend(originUrl);
    }

    return this.sendEmailVerificationFromClient(
      authenticationUser,
    );
  }

  static async sendEmailVerificationFromBackend(originUrl) {
    const response = await graphqlClient.mutate({
      mutation: gql`
        mutation AUTH_SEND_EMAIL_ADDRESS_VERIFICATION_EMAIL($originUrl: String!) {
          authSendEmailAddressVerificationEmail(originUrl: $originUrl)
        }
      `,
      variables: {
        originUrl
      },
    });

    return response.data
      .authSendEmailAddressVerificationEmail;
  }

  static async sendEmailVerificationFromClient(
    authenticationUser,
  ) {
    return authenticationUser.sendEmailVerification();
  }

  static async sendPasswordResetEmail(email, originUrl) {
    if (await this.isEmailConfigured()) {
      return this.sendPasswordResetEmailFromBackend(email, originUrl);
    }

    return this.sendPasswordResetEmailFromClient(email);
  }

  static async sendPasswordResetEmailFromBackend(email, originUrl) {
    const response = await graphqlClient.mutate({
      mutation: gql`
        mutation AUTH_SEND_PASSWORD_RESET_EMAIL(
          $email: String!
          $originUrl: String!
        ) {
          authSendPasswordResetEmail(email: $email, originUrl: $originUrl)
        }
      `,
      variables: {
        email,
        originUrl
      },
    });

    return response.data.authSendPasswordResetEmail;
  }

  static async sendPasswordResetEmailFromClient(email) {
    return firebase.auth().sendPasswordResetEmail(email);
  }

  static async registerWithEmailAndPassword(
    email,
    password,
  ) {
    const credentials = await firebase
      .auth()
      .createUserWithEmailAndPassword(email, password);
    this.sendEmailVerification(credentials.user);
    return credentials.user;
  }

  static async signinWithSocial(
    provider,
    rememberMe = false,
  ) {
    const persistence = rememberMe
      ? firebase.auth.Auth.Persistence.LOCAL
      : firebase.auth.Auth.Persistence.SESSION;

    await firebase.auth().setPersistence(persistence);

    const providers = {
      google: firebase.auth.GoogleAuthProvider,
      facebook: firebase.auth.FacebookAuthProvider,
      twitter: firebase.auth.TwitterAuthProvider,
    };

    return firebase
      .auth()
      .signInWithPopup(new providers[provider]());
  }

  static async signinWithEmailAndPassword(
    email,
    password,
    rememberMe = false,
  ) {
    const persistence = rememberMe
      ? firebase.auth.Auth.Persistence.LOCAL
      : firebase.auth.Auth.Persistence.SESSION;

    await firebase.auth().setPersistence(persistence);

    const r = await firebase
      .auth()
      .signInWithEmailAndPassword(email, password);
    const token = await r.user.getIdToken()
    console.log(token);
    return firebase
      .auth()
      .signInWithEmailAndPassword(email, password);
  }

  static async fetchMe() {
    const response = await graphqlClient.query({
      query: gql`
        {
          authMe {
            id
            isAdmin
            authenticationUid
            fullName
            firstName
            lastName
            gender
            birthdate {
              day
              month
              year
            }
            phoneNumber {
              prefix
              number
            }
            email
            roles
            permissions
            maxManage {
              type
              propertyId
              subpropertyId
            }
            avatars {
              id
              name
              publicUrl
            }
          }
        }
      `,
    });
    return response.data.authMe;
  }

  static async isEmailConfigured() {
    const response = await graphqlClient.query({
      query: gql`
        {
          authIsEmailConfigured
        }
      `,
    });

    return response.data.authIsEmailConfigured;
  }

  static async reauthenticateWithStorageToken() {
    try {
      const response = await graphqlClient.query({
        query: gql`
          {
            authStorageToken
          }
        `,
      });

      const token = response.data.authStorageToken;
      return await firebase.auth().signInWithCustomToken(token);
    } catch (error) {
      console.error(error);
    }
  }

  static signout() {
    return firebase.auth().signOut();
  }

  static async updateProfile( profile ) {
    const response = await graphqlClient.mutate({
      mutation: gql`
        mutation AUTH_UPDATE_PROFILE(
          $profile: UserProfileInput!
        ) {
          authUpdateProfile(profile: $profile)
        }
      `,

      variables: {
        profile
      },
    });

    return response.data.authUpdateProfile;
  }

  static async updateLastLogin() {
    const response = await graphqlClient.mutate({
      mutation: gql`
        mutation AUTH_UPDATE_LAST_LOGIN {
          authUpdateLastLogin
        }
      `,
    });

    return response.data.authUpdateLastLogin;
  }
}
